import React from 'react'
import { Alert, Box, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'

import PageHeader from '../../components/system/PageHeader'
import { BRAND_GRADIENT } from '../../components/social-media/design-tokens'
import { useInfluencerUploadHistory } from '../../hooks/socialdash/useInfluencerUploads'
import ProductTabs, { ADMIN_TAB } from './ProductTabs'
import AdminViewTabs from './AdminViewTabs'
import InfluencerUpload from './InfluencerUpload'

const n = (v: number) => v.toLocaleString('en-GB')

/** "20 Oct 2026, 16:05 UTC": uploads are stamped in UTC like the rest of Social Media. */
const when = (ms: number) =>
  `${new Date(ms).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  })} UTC`

/**
 * Influencer data — the Admin view where Sky's influencer export is uploaded
 * (#1694 follow-up). The figures feed the dashboards' Source switch
 * (Influencer) and nothing else. Admins only, like Post data: only admins are
 * offered the Admin tab, and the server refuses the upload and the history to
 * anyone else. See docs/sky-influencer-data.md.
 */
export const InfluencerDataPage: React.FC = () => {
  const { uploads, isLoading, error } = useInfluencerUploadHistory()

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', bgcolor: 'background.default' }}>
      <ProductTabs product={ADMIN_TAB} />
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <PageHeader
          title="Influencer data"
          gradient={BRAND_GRADIENT}
          meta={
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Keep the dashboards’ Influencer figures up to date by uploading Sky’s latest influencer export.
            </Typography>
          }
        />
        <AdminViewTabs view="influencer-data" />

        <Box sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <InfluencerUpload />

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }} data-testid="influencer-upload-history">
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Uploads
            </Typography>
            {error && <Alert severity="error">{error.message}</Alert>}
            {!error && !isLoading && uploads.length === 0 && (
              <Typography variant="body2" color="text.secondary" data-testid="influencer-upload-history-empty">
                No file has been uploaded here yet.
              </Typography>
            )}
            {uploads.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell align="right">Rows</TableCell>
                    <TableCell align="right">Duplicates folded</TableCell>
                    <TableCell align="right">Posts</TableCell>
                    <TableCell align="right">New</TableCell>
                    <TableCell align="right">Updated</TableCell>
                    <TableCell>Published</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploads.map((u) => (
                    <TableRow key={u.id} data-testid={`influencer-upload-history-${u.id}`}>
                      <TableCell>{when(u.uploadedAt)}</TableCell>
                      <TableCell>{u.fileName}</TableCell>
                      <TableCell>{u.uploadedBy}</TableCell>
                      <TableCell align="right">{n(u.summary.rows)}</TableCell>
                      <TableCell align="right">{n(u.summary.duplicateListings)}</TableCell>
                      <TableCell align="right">{n(u.summary.posts)}</TableCell>
                      <TableCell align="right">{n(u.summary.new)}</TableCell>
                      <TableCell align="right">{n(u.summary.updated)}</TableCell>
                      <TableCell>
                        {u.summary.from} to {u.summary.to}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  )
}

export default InfluencerDataPage
